import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { Link } from 'react-router-dom'
import { encodePath } from '../helpers'
import { useGuild } from '../config/context/guild'
import { hasAccessToContent } from './ProtectedContent'
import { AccessLevel, Section } from 'app/frontend/types/custom'
import { cn } from '@/lib/utils'
import { useHasSidebar } from '../hooks/useHasSidebar'

export type MenuItem = {
  name: string | React.ReactNode
  path: string
  width: number
  exact?: boolean
  section?: Section
  requirement?: AccessLevel
  children?: (Omit<MenuItem, 'children' | 'width'> & { width?: number })[]
}

export type MenuItemProps = {
  menuItems: MenuItem[]
  placement: 'subheader' | 'header'
  usedWidth?: number
}

const EntityMenu = ({ menuItems, placement, usedWidth = 0 }: MenuItemProps) => {
  const { teamReference, guild } = useGuild()
  const hasSidebar = useHasSidebar()

  const menuItemsWithAccess = menuItems.filter((item) => {
    return hasAccessToContent(
      item.requirement ?? 'none',
      item.section ?? 'base',
      guild,
      teamReference,
    ).hasAccess
  })

  const fitItemsWithinWidth = (items: typeof menuItems, maxWidth: number) => {
    if (hasSidebar) {
      return items
    }

    let totalWidth = 0
    const visibleItems: typeof menuItems = []

    for (const item of items) {
      if (totalWidth + item.width > maxWidth) break
      visibleItems.push(item)
      totalWidth += item.width
    }

    return visibleItems
  }

  const availableWidth =
    (window.innerWidth >= 640
      ? window.innerWidth - 140
      : window.innerWidth - (placement === 'header' ? 0 : 90)) - usedWidth

  const visibleMenuItems = fitItemsWithinWidth(
    menuItemsWithAccess,
    availableWidth,
  )

  const invisibleMenuItems = menuItemsWithAccess.slice(visibleMenuItems.length)
  if (invisibleMenuItems.length === 1 && visibleMenuItems.length > 0) {
    invisibleMenuItems.push(visibleMenuItems.pop() as typeof menuItems[number])
  }

  return (
    <>
      {visibleMenuItems.map((item) => {
        const encodedPath = encodePath(item.path)
        const childrenWithAccess =
          item.children?.filter((child) => {
            return hasAccessToContent(
              child.requirement ?? item.requirement ?? 'none',
              child.section ?? item.section ?? 'base',
              guild,
              teamReference,
            ).hasAccess
          }) ?? []

        const target =
          childrenWithAccess.length === 1 ? childrenWithAccess[0] : item
        const encodedTargetPath = encodePath(target.path)

        return (
          <div key={item.path}>
            {childrenWithAccess.length > 1 && !hasSidebar ? (
              <NavigationMenu delayDuration={50}>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      onClick={(e) => e.preventDefault()}
                      className={cn(
                        'cursor-default pr-2 rounded-none',
                        (
                          item.exact
                            ? window.location.pathname === encodedPath
                            : window.location.pathname.includes(encodedPath)
                        )
                          ? 'border-b-foreground bg-card'
                          : '',
                        placement === 'header'
                          ? hasSidebar
                            ? 'bg-transparent py-0'
                            : 'h-14 bg-transparent'
                          : 'bg-background mb-[-2px] border-b-2',
                      )}
                    >
                      {item.name}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div>
                        {childrenWithAccess.map((child) => {
                          const encodedChildPath = encodePath(child.path)
                          return (
                            <NavigationMenuLink
                              key={child.path}
                              active={window.location.pathname.includes(
                                encodedChildPath,
                              )}
                              className={`${navigationMenuTriggerStyle()} hover:bg-accent rounded-none last:rounded-b-md min-w-full data-active:text-primary`}
                              asChild
                            >
                              <Link
                                to={child.path}
                                className='whitespace-nowrap'
                              >
                                {child.name}
                              </Link>
                            </NavigationMenuLink>
                          )
                        })}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            ) : (
              <Link
                to={target.path}
                data-active={
                  (target.exact
                    ? window.location.pathname === encodedTargetPath
                    : window.location.pathname.includes(encodedTargetPath)) ||
                  undefined
                }
                className={cn(
                  navigationMenuTriggerStyle(),
                  'bg-transparent rounded-none whitespace-nowrap',
                  placement === 'header'
                    ? hasSidebar
                      ? 'h-9 border my-1 rounded-md bg-card-nested data-active:text-primary hover:text-accent-foreground transition-colors w-full justify-start'
                      : 'h-14 data-active:text-primary hover:text-accent-foreground transition-colors'
                    : 'mb-[-2px] border-b-2 border-b-transparent hover:border-b-border data-active:border-b-foreground',
                )}
              >
                {target.name}
              </Link>
            )}
          </div>
        )
      })}

      {invisibleMenuItems.length > 0 ? (
        <NavigationMenu
          delayDuration={50}
          viewportClassName='left-auto right-0'
        >
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuTrigger
                onClick={(e) => e.preventDefault()}
                className={cn(
                  'cursor-default pr-2 rounded-none',
                  placement === 'header'
                    ? 'h-14 bg-transparent'
                    : 'bg-background mb-[-2px] border-b-2',
                )}
              >
                {visibleMenuItems.length > 0 ? 'More' : 'Menu'}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className='flex flex-col'>
                  {invisibleMenuItems.map((item) => {
                    const encodedPath = encodePath(item.path)
                    return (
                      <NavigationMenuLink
                        key={item.path}
                        active={
                          item.exact
                            ? window.location.pathname === encodedPath
                            : window.location.pathname.includes(encodedPath)
                        }
                        className={`${navigationMenuTriggerStyle()} rounded-none last:rounded-b-md min-w-full`}
                        asChild
                      >
                        <Link to={item.path}>{item.name}</Link>
                      </NavigationMenuLink>
                    )
                  })}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      ) : null}
    </>
  )
}

export default EntityMenu
